<template>
  <StaticPageLayout
    title="Hizmet Sözleşmesi - INSATEQ"
    description="INSATEQ SaaS platformu hizmet sözleşmesi. Uzaktan hizmet satın alma, abonelik koşulları, ödeme ve iade politikası."
  >
    <!-- Hero -->
    <div class="bg-gradient-to-br from-blue-600 to-indigo-700 py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-3xl md:text-4xl font-bold text-white mb-4">
          Hizmet Sözleşmesi
        </h1>
        <p class="text-blue-100 text-lg mb-2">
          Uzaktan Hizmet Satın Alma Sözleşmesi
        </p>
        <p class="text-blue-200 text-sm">
          Son güncelleme: {{ lastUpdate }}
        </p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="prose prose-lg max-w-none">

        <!-- Section 1: Parties -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">1</span>
            Taraflar ve Tanımlar
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">1.1. Hizmet Sağlayıcı</h3>
          <div class="bg-gray-50 rounded-xl p-6 mb-4">
            <p class="text-gray-700"><strong>Ünvan:</strong> INSATEQ Yazılım ve Teknoloji</p>
            <p class="text-gray-700"><strong>Adres:</strong> Atakent Mah. Mithatpaşa Cd. No:116, Ceotekmer Teknoloji Merkezi, 34760 Ümraniye/İstanbul</p>
            <p class="text-gray-700"><strong>E-posta:</strong> destek@insateq.com</p>
          </div>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">1.2. Abone (Kullanıcı)</h3>
          <p class="text-gray-600 leading-relaxed">
            Bu sözleşmeyi onaylayan ve INSATEQ hizmetlerini kullanan gerçek veya tüzel kişi.
            Abonenin kayıt sırasında beyan ettiği bilgiler geçerli kabul edilir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">1.3. Tanımlar</h3>
          <div class="bg-gray-50 rounded-xl p-6 space-y-3">
            <p class="text-gray-700"><strong>"Platform":</strong> insateq.com alan adı ve alt alan adları üzerinden sunulan web tabanlı yazılım hizmeti (SaaS)</p>
            <p class="text-gray-700"><strong>"Organizasyon":</strong> Abonenin oluşturup yönettiği iş birimi (tenant)</p>
            <p class="text-gray-700"><strong>"Kullanıcı":</strong> Organizasyon altında tanımlanan ve platforma erişim yetkisi verilen kişi</p>
            <p class="text-gray-700"><strong>"Abonelik Dönemi":</strong> Aylık veya yıllık faturalandırma süresi</p>
            <p class="text-gray-700"><strong>"Deneme Süresi":</strong> Ücretli planların başlangıcında sunulan ücretsiz kullanım süresi</p>
            <p class="text-gray-700"><strong>"Veri":</strong> Abonenin platforma yüklediği her türlü içerik, dosya ve bilgi</p>
          </div>
        </section>

        <!-- Section 2: Service Description -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">2</span>
            Hizmetin Tanımı ve Kapsamı
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">2.1. Hizmet Modeli</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ, bulut tabanlı (SaaS - Software as a Service) bir inşaat ve proje yönetim platformudur.
            Hizmet, internet üzerinden erişilen bir yazılım olarak sunulmakta olup, herhangi bir fiziksel
            ürün teslimi içerilmemektedir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">2.2. Sunulan Özellikler</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Platform, seçilen abonelik planına göre aşağıdaki modüllerden bir veya daha fazlasını içerebilir:
          </p>
          <div class="grid md:grid-cols-2 gap-3">
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Proje Yönetimi</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Personel ve İK Yönetimi</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Puantaj ve Mesai Takibi</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Keşif ve Metraj</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Hakediş Yönetimi</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Finans ve Muhasebe</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">İş Sağlığı ve Güvenliği</div>
            <div class="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">Raporlama ve Analitik</div>
          </div>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">2.3. Hizmet Erişilebilirliği</h3>
          <p class="text-gray-600 leading-relaxed">
            INSATEQ, platformun ayda en az %99.5 oranında erişilebilirliğini hedefler (SLA).
            Planlı bakımlar en az 24 saat önceden bildirilir. Mücbir sebep halleri ve planlı bakımlar
            erişilebilirlik hesaplamasına dahil edilmez.
          </p>
        </section>

        <!-- Section 3: Subscription and Pricing -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">3</span>
            Abonelik ve Ücretlendirme
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">3.1. Abonelik Planları</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ, farklı ihtiyaçlara yönelik çeşitli abonelik planları sunmaktadır.
            Güncel plan özellikleri ve fiyatları fiyatlandırma sayfamızda yer almaktadır.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">3.2. Faturalandırma Dönemleri</h3>
          <div class="grid md:grid-cols-2 gap-4 mb-4">
            <div class="bg-gray-50 rounded-xl p-5">
              <h4 class="font-semibold text-gray-900 mb-2">Aylık Abonelik</h4>
              <ul class="text-gray-600 text-sm space-y-1">
                <li>- Her ay otomatik yenilenir</li>
                <li>- İstediğiniz zaman iptal edilebilir</li>
                <li>- İptal, dönem sonunda geçerli olur</li>
              </ul>
            </div>
            <div class="bg-gray-50 rounded-xl p-5">
              <h4 class="font-semibold text-gray-900 mb-2">Yıllık Abonelik</h4>
              <ul class="text-gray-600 text-sm space-y-1">
                <li>- 12 aylık ödeme peşin alınır</li>
                <li>- Aylık fiyata göre indirimli</li>
                <li>- <strong class="text-red-600">İade yapılmaz</strong></li>
              </ul>
            </div>
          </div>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">3.3. Deneme Süresi</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Yeni kayıt olan abonelere belirtilen süre boyunca (genellikle 14 gün) ücretsiz deneme süresi
            tanınır. Deneme süresi bitiminde otomatik faturalandırma başlar. Deneme süresinde iptal ederseniz
            herhangi bir ücret alınmaz.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">3.4. Fiyat Değişiklikleri</h3>
          <p class="text-gray-600 leading-relaxed">
            INSATEQ, fiyatlarını değiştirme hakkını saklı tutar. Fiyat değişiklikleri mevcut abonelik
            döneminizi etkilemez ve yenileme öncesinde en az 30 gün önce bildirilir.
          </p>
        </section>

        <!-- Section 4: Payment Terms - CRITICAL -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">4</span>
            Ödeme Koşulları
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">4.1. Ödeme Yöntemleri</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Ödemeler kredi kartı, banka kartı veya havale/EFT yoluyla yapılabilir.
            Online ödemeler iyzico ödeme altyapısı üzerinden güvenli şekilde işlenir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">4.2. Otomatik Yenileme</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Abonelikler, iptal edilmediği sürece otomatik olarak yenilenir. Yenileme öncesi
            kayıtlı ödeme yönteminizden tahsilat yapılır.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">4.3. Geciken Ödemeler</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Ödeme alınamaması halinde hesabınız askıya alınabilir. 15 gün içinde ödeme
            yapılmazsa hesap kapatılabilir ve verileriniz silinebilir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">4.4. İade Politikası</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Yıllık plan satın aldığınızda 12 aylık hizmet bedelini peşin ödediğinizi ve bu ödemenin
            geri alınamayacağını kabul etmiş sayılırsınız.
          </p>
          <p class="text-gray-600 leading-relaxed mb-4">
            6502 sayılı Tüketicinin Korunması Hakkında Kanun'un 50. maddesi ve Mesafeli Sözleşmeler
            Yönetmeliği'nin 15. maddesinin (g) bendi gereğince, elektronik ortamda anında ifa
            edilen hizmetlerde ve dijital içerik teslimlerinde cayma hakkı uygulanmaz.
          </p>
          <p class="text-gray-600 leading-relaxed mb-4">
            Platform hizmetleri, abonelik başladığı anda anında aktif hale geldiğinden ve
            sürekli erişim sağlanan dijital bir hizmet olduğu için cayma hakkı kapsamı dışındadır.
          </p>
          <p class="text-gray-600 leading-relaxed">
            Aylık aboneliklerde, iptal talebiniz mevcut dönem sonunda gerçekleşir. Kullanılmamış
            günler için kısmi iade yapılmaz.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">4.5. Fatura ve Belgeler</h3>
          <p class="text-gray-600 leading-relaxed">
            Her ödeme sonrası e-fatura/e-arşiv fatura kayıtlı e-posta adresinize gönderilir.
            Faturalar ayrıca platform üzerinden de erişilebilir.
          </p>
        </section>

        <!-- Section 5: Data Protection -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">5</span>
            Veri Koruma ve Gizlilik
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">5.1. KVKK Uyumluluğu</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ, 6698 sayılı Kişisel Verilerin Korunması Kanunu'na uygun olarak faaliyet gösterir.
            Kişisel verilerinizin işlenmesine ilişkin detaylı bilgi için
            <a href="/kvkk" class="text-blue-600 hover:text-blue-700 underline">KVKK Aydınlatma Metni</a>'ni inceleyiniz.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">5.2. Veri Mülkiyeti</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Platforma yüklediğiniz tüm veriler size aittir. INSATEQ bu verileri yalnızca hizmet sunmak
            amacıyla işler ve üçüncü taraflarla paylaşmaz (yasal zorunluluklar hariç).
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">5.3. Veri Güvenliği</h3>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Tüm veri transferleri SSL/TLS ile şifrelenir</li>
            <li>Veriler Türkiye'de konumlu sunucularda saklanır</li>
            <li>Düzenli yedekleme yapılır</li>
            <li>Erişim kontrolü ve log kaydı tutulur</li>
          </ul>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">5.4. Veri Silme</h3>
          <p class="text-gray-600 leading-relaxed">
            Aboneliğiniz sona erdiği takdirde verileriniz 30 gün boyunca saklanır. Bu süre içinde talep
            ederseniz verilerinizi dışarı aktarabilirsiniz. 30 gün sonunda veriler kalıcı olarak silinir.
          </p>
        </section>

        <!-- Section 6: User Obligations -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">6</span>
            Abone Yükümlülükleri
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">6.1. Hesap Güvenliği</h3>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Hesap bilgilerinizin gizliliğinden siz sorumlusunuz</li>
            <li>Güçlü şifre kullanın ve düzenli değiştirin</li>
            <li>Şüpheli erişim fark ettiğinizde derhal bildirin</li>
            <li>Hesabınız üzerinden yapılan tüm işlemlerden sorumlusunuz</li>
          </ul>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">6.2. Yasak Faaliyetler</h3>
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <ul class="text-red-700 text-sm space-y-1">
              <li>- Yasadışı amaçlarla kullanım</li>
              <li>- Sisteme yetkisiz erişim denemeleri</li>
              <li>- Zararlı yazılım yükleme veya dağıtma</li>
              <li>- Diğer abonelerin hizmetini engelleme</li>
              <li>- Fikri mülkiyet haklarını ihlal etme</li>
              <li>- Platform API'lerini kötüye kullanma</li>
            </ul>
          </div>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">6.3. İçerik Sorumluluğu</h3>
          <p class="text-gray-600 leading-relaxed">
            Platforma yüklediğiniz içeriklerden tamamen siz sorumlusunuz. Yasal olmayan, hakaret
            içeren veya üçüncü taraf haklarını ihlal eden içerikler nedeniyle INSATEQ sorumluluk kabul etmez.
          </p>
        </section>

        <!-- Section 7: Intellectual Property -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">7</span>
            Fikri Mülkiyet Hakları
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">7.1. Platform Hakları</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ platformu, yazılımı, tasarımı, logoları ve tüm ilgili fikri mülkiyet hakları
            INSATEQ'e aittir. Bu haklar telif hakkı, patent ve marka kanunlarıyla korunmaktadır.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">7.2. Lisans</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Abonelik süresince platformu kullanmak için sınırlı, devredilemez, münhasır olmayan
            bir lisans verilir. Bu lisans abonelik sona erdiğinde otomatik olarak sona erer.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">7.3. Abone İçerikleri</h3>
          <p class="text-gray-600 leading-relaxed">
            Yüklediğiniz içerikler üzerindeki fikri mülkiyet hakları size aittir. Ancak INSATEQ'e
            bu içerikleri hizmet sunmak amacıyla işleme, saklama ve gösterme lisansı vermiş sayılırsınız.
          </p>
        </section>

        <!-- Section 8: Limitation of Liability -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">8</span>
            Sorumluluk Sınırlaması
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">8.1. Hizmet Garantisi</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Platform "olduğu gibi" sunulmaktadır. INSATEQ, platformun kesintisiz veya hatasız
            çalışacağını garanti etmez. Planlı bakımlar ve teknik aksaklıklar yaşanabilir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">8.2. Sorumluluk Sınırı</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ'in bu sözleşmeden doğan toplam sorumluluğu, son 12 ayda ödediğiniz abonelik
            ücretini aşmayacaktır. Aşağıdaki durumlardan INSATEQ sorumlu tutulamaz:
          </p>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Dolaylı, arızi veya cezai zararlar</li>
            <li>Kâr kaybı, veri kaybı veya iş kesintisi</li>
            <li>Üçüncü taraf hizmetlerinden kaynaklanan sorunlar</li>
            <li>Kullanıcı hataları sonucu oluşan zararlar</li>
            <li>Mücbir sebep hallerinden kaynaklanan zararlar</li>
          </ul>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">8.3. Tazminat</h3>
          <p class="text-gray-600 leading-relaxed">
            Abone, bu sözleşmeyi ihlal etmesi veya platform kullanımı nedeniyle üçüncü tarafların
            INSATEQ'e yöneltebileceği tüm talep, dava ve masraflardan INSATEQ'i muaf tutmayı kabul eder.
          </p>
        </section>

        <!-- Section 9: Termination -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">9</span>
            Fesih ve Sonlandırma
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">9.1. Abone Tarafından İptal</h3>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Aylık abonelikleri istediğiniz zaman iptal edebilirsiniz</li>
            <li>İptal, mevcut dönem sonunda geçerli olur</li>
            <li>İptal tarihine kadar platforma erişim devam eder</li>
            <li><strong>Yıllık aboneliklerde erken iptal halinde iade yapılmaz</strong></li>
          </ul>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">9.2. INSATEQ Tarafından Fesih</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Aşağıdaki durumlarda hesabınız derhal askıya alınabilir veya feshedilebilir:
          </p>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Sözleşme şartlarının ihlali</li>
            <li>Yasadışı faaliyetler</li>
            <li>Ödeme yükümlülüklerinin yerine getirilmemesi</li>
            <li>Platform güvenliğini tehdit eden davranışlar</li>
          </ul>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">9.3. Fesih Sonrası</h3>
          <p class="text-gray-600 leading-relaxed">
            Fesih sonrasında verileriniz 30 gün boyunca saklanır. Bu süre içinde dışarı aktarım
            talep edebilirsiniz. 30 gün sonra tüm veriler kalıcı olarak silinir.
          </p>
        </section>

        <!-- Section 10: Force Majeure -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">10</span>
            Mücbir Sebepler
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Aşağıdaki durumlar mücbir sebep olarak kabul edilir ve INSATEQ bu durumlardan kaynaklanan
            gecikme veya ifa imkânsızlığı nedeniyle sorumlu tutulamaz:
          </p>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Doğal afetler (deprem, sel, yangın vb.)</li>
            <li>Savaş, terör, isyan veya sivil kargaşa</li>
            <li>Devlet müdahalesi veya yasal kısıtlamalar</li>
            <li>Salgın hastalık</li>
            <li>İnternet altyapısındaki genel arızalar</li>
            <li>Siber saldırılar</li>
          </ul>
        </section>

        <!-- Section 11: Amendments -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">11</span>
            Değişiklikler
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ, bu sözleşmeyi herhangi bir zamanda değiştirme hakkını saklı tutar.
            Önemli değişiklikler en az 30 gün öncesinden e-posta ile bildirilir.
          </p>
          <p class="text-gray-600 leading-relaxed">
            Değişiklik sonrasında platformu kullanmaya devam etmeniz, yeni şartları kabul ettiğiniz
            anlamına gelir. Kabul etmemeniz halinde aboneliğinizi iptal edebilirsiniz.
          </p>
        </section>

        <!-- Section 12: Dispute Resolution -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">12</span>
            Uyuşmazlıkların Çözümü
          </h2>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">12.1. Geçerli Hukuk</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Bu sözleşme Türkiye Cumhuriyeti hukukuna tabidir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">12.2. Yetkili Mahkeme</h3>
          <p class="text-gray-600 leading-relaxed mb-4">
            Bu sözleşmeden doğan uyuşmazlıklarda İstanbul Mahkemeleri ve İcra Daireleri yetkilidir.
          </p>

          <h3 class="text-lg font-medium text-gray-800 mt-6 mb-3">12.3. Tüketici Hakem Heyeti</h3>
          <p class="text-gray-600 leading-relaxed">
            Tüketici sıfatındaki aboneler, yürürlükteki parasal sınırlara göre ilgili
            Tüketici Hakem Heyetleri'ne başvuru hakkına sahiptir.
          </p>
        </section>

        <!-- Section 13: Contact -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">13</span>
            İletişim
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Bu sözleşme hakkındaki sorularınız için bizimle iletişime geçebilirsiniz:
          </p>
          <div class="bg-blue-50 rounded-xl p-6 space-y-3">
            <div class="flex items-center">
              <svg class="w-5 h-5 text-blue-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href="mailto:destek@insateq.com" class="text-blue-600 hover:text-blue-700 font-medium">destek@insateq.com</a>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="text-gray-700">Atakent Mah. Mithatpaşa Cd. No:116<br>Ceotekmer Teknoloji Merkezi<br>34760 Ümraniye/İstanbul</span>
            </div>
          </div>
        </section>

        <!-- Final Acceptance -->
        <div class="bg-gray-100 rounded-xl p-6 mt-10 border border-gray-200">
          <p class="text-gray-700 text-center">
            INSATEQ platformuna kayıt olarak veya hizmetlerimizi kullanarak yukarıdaki tüm
            şartları okuduğunuzu, anladığınızı ve kabul ettiğinizi beyan etmiş sayılırsınız.
          </p>
        </div>

      </div>
    </div>
  </StaticPageLayout>
</template>

<script setup>
import StaticPageLayout from '@/Layouts/StaticPageLayout.vue'

const lastUpdate = new Date().toLocaleDateString('tr-TR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})
</script>
